import { createApi } from '@reduxjs/toolkit/query/react';
import { pensionBaseQuery } from '../../lib/axiosBaseQuery';
import { paramBuilder } from '../../utils/common';

const asList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.pfaList)) return payload.pfaList;
  return [];
};

const asUploadPfaSummaryList = (payload: any): any[] => {
  const records = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.summary)
        ? payload.summary
        : [];
  if (!Array.isArray(records)) return [];

  return records.flatMap((record) =>
    Array.isArray(record?.summary) ? record.summary : [record]
  );
};

export const paymentsApi = createApi({
  reducerPath: 'paymentsApi',
  baseQuery: pensionBaseQuery,
  tagTypes: ['PaymentHistory', 'PaymentSummary', 'PaymentSchedule', 'PFAList', 'EmployerInfo', 'PendingApprovals'],
  endpoints: (builder) => ({
    getFileUploadsSummary: builder.query<any, string>({
      query: (employerCode) => ({
        url: `api/v1/pension/Payment/uploads/summary`,
        method: 'GET',
        params: { employerCode },
      }),
      providesTags: ['PaymentSummary'],
    }),
    getFileUploadReferenceSummary: builder.query<any, string>({
      query: (ref) => ({
        url: `api/v1/pension/Payment/uploads/${ref}/summary`,
        method: 'GET',
      }),
    }),
    getPFAList: builder.query<any, void>({
      query: () => ({
        url: `api/v1/pension/Payment/pfa/list`,
        method: 'GET',
      }),
      transformResponse: asList,
      providesTags: ['PFAList'],
    }),
    getUploadPfaList: builder.query<any, string>({
      query: (ref) => ({
        url: `api/v1/pension/Payment/schedule/upload/${ref}`,
        method: 'GET',
      }),
      transformResponse: asUploadPfaSummaryList,
    }),
    getUploadPfaItems: builder.query<any, { ref: string; pfaCode: string; page?: number; pageSize?: number }>({
      query: ({ ref, pfaCode, page = 1, pageSize = 500 }) => ({
        url: `api/v1/pension/Payment/uploads/${ref}/${pfaCode}/items`,
        method: 'GET',
        params: { page, pageSize },
      }),
    }),
    getUploadBeneficiaries: builder.query<any, { ref: string; page?: number; pageSize?: number }>({
      query: ({ ref, page = 1, pageSize = 500 }) => ({
        url: `api/v1/pension/Payment/schedule/upload/${ref}/beneficiaries`,
        method: 'GET',
        params: { page, pageSize },
      }),
    }),
    // Full export of every beneficiary in the upload (all pages), generated server-side as .xlsx
    exportUploadBeneficiaries: builder.mutation<Blob, string>({
      query: (ref) => ({
        url: `api/v1/pension/Payment/schedule/upload/${ref}/beneficiaries/export`,
        method: 'GET',
        responseType: 'blob',
        suppressGlobalErrorToast: true,
      }),
    }),
    validateUpload: builder.mutation<any, string>({
      query: (ref) => ({
        url: `api/v1/pension/Payment/uploads/${ref}/validate`,
        method: 'POST',
      }),
    }),
    confirmUpload: builder.mutation<any, string>({
      query: (ref) => ({
        url: `api/v1/Approval/upload/transaction/confirm/${ref}`,
        method: 'GET',
      }),
    }),
    confirmUploadOtp: builder.mutation<any, { ref: string; otp: string, otherDetails?: { approve?: boolean, reason?: string } }>({
      query: ({ ref, otp, otherDetails }) => ({
        url: `api/v1/Approval/upload/transaction/confirm/${ref}`,
        method: 'PUT',
        data: { otp, otherDetails },
      }),
      invalidatesTags: ['PaymentSummary', 'PendingApprovals'],
    }),
    deleteUpload: builder.mutation<any, string>({
      query: (ref) => ({
        url: `api/v1/pension/Payment/schedule/upload/${ref}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentSummary'],
    }),
    getPaymentHistory: builder.query<any, Record<string, string> | void>({
      query: (period) => {
        let params = '';
        if (period) {
          const searchParams = new URLSearchParams(period);
          params = `?${searchParams.toString()}`;
        }
        return {
          url: `api/v1/pension/payment/history${params}`,
          method: 'GET',
        };
      },
      providesTags: ['PaymentHistory'],
    }),
    getPFABeneficiaries: builder.query<any, { id: string; page: number; pageCount: number }>({
      query: ({ id, page, pageCount }) => ({
        url: `api/v1/pension/Payment/${id}/pfa/items`,
        method: 'GET',
        params: { page, pageSize: pageCount },
      }),
    }),
    getPayments: builder.query<any, string>({
      query: (id) => ({
        url: `api/v1/Payment/${id}/items`,
        method: 'GET',
      }),
    }),
    createSchedule: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/pension/Payment/schedule/create`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    uploadScheduleFile: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/pension/Payment/schedule/file/upload`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    checkScheduleFileUploadStatus: builder.query<any, string>({
      query: (referenceId) => ({
        url: `api/v1/pension/Payment/schedule/file/upload/${referenceId}/status`,
        method: 'GET',
      }),
    }),
    removeItemFromSchedule: builder.mutation<any, { beneficiaryId: string; scheduleId: string }>({
      query: ({ beneficiaryId, scheduleId }) => ({
        url: `api/v1/pension/Payment/schedule/items/remove`,
        method: 'PUT',
        data: { beneficiaryId, scheduleId },
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    addItemToSchedule: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/pension/Payment/schedule/items/add`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    pfaList: builder.query<any, void>({
      query: () => ({
        url: `api/v1/pension/Payment/pfa/list`,
        method: 'GET',
      }),
      transformResponse: asList,
      providesTags: ['PFAList'],
    }),
    getEmployerInfo: builder.query<any, string>({
      query: (employerCode) => ({
        url: `api/v1/pension/Payment/employer/${employerCode}/info`,
        method: 'GET',
      }),
      providesTags: ['EmployerInfo'],
    }),
    getPFAInfo: builder.query<any, string>({
      query: (pfaCode) => ({
        url: `api/v1/Payment/pfainfo/${pfaCode}`,
        method: 'GET',
      }),
    }),
    getSchedule: builder.query<any, string>({
      query: (id) => ({
        url: `api/v1/pension/Payment/schedule/${id}`,
        method: 'GET',
      }),
      providesTags: ['PaymentSchedule'],
    }),
    confirmSchedule: builder.query<any, string>({
      query: (scheduleId) => ({
        url: `api/v1/pension/Payment/confirm/${scheduleId}`,
        method: 'GET',
      }),
    }),
    validatePaymentOTP: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/pension/Payment/validate`,
        method: 'POST',
        data,
      }),
    }),
    validatePayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `api/v1/pension/Payment/schedule/validate/${id}`,
        method: 'POST',
      }),
    }),
    reValidateCardPayment: builder.mutation<any, string>({
      query: (id) => ({
        url: `api/v1/pension/Payment/schedule/${id}/verify/card-payment`,
        method: 'GET',
      }),
    }),
    updateScheduleBeneficiary: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/pension/Payment/schedule/update/item`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    uploadEvidenceOfPayment: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/Payment/offline/docs/upload`,
        method: 'POST',
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    }),
    downloadEvidenceOfPayment: builder.mutation<Blob, string>({
      query: (itemId) => ({
        url: `api/v1/Payment/offline/docs/download/${itemId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    deleteRemitance: builder.mutation<any, string>({
      query: (id) => ({
        url: `api/v1/pension/Payment/schedule/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentSchedule'],
    }),
    getPeymentScheduleBeneficiaries: builder.query<any, { scheduleId: string; page?: number; pageSize?: number }>({
      query: ({ scheduleId, page = 1, pageSize = 500 }) => ({
        url: `api/v1/pension/Payment/schedule/${scheduleId}/items`,
        method: 'GET',
        params: { page, pageSize },
      }),
    }),
    getPendingPayents: builder.query<any, void>({
      query: () => ({
        url: `api/v1/Approval/transactions`,
        method: 'GET',
      }),
      providesTags: ['PendingApprovals'],
    }),
    getPaymentApprovalOtp: builder.query<any, string>({
      query: (scheduleId) => ({
        url: `api/v1/Approval/transaction/confirm/${scheduleId}`,
        method: 'GET',
      }),
    }),
    approvePaymentOtp: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: `api/v1/Approval/transaction/confirm`,
        method: 'PUT',
        data,
      }),
    }),
    getPaymentSummary: builder.query<any, { period: string; year: string; employerCode: string }>({
      query: ({ period, year, employerCode }) => ({
        url: `api/v1/pension/Payment/schedule/payments/summary`,
        method: 'GET',
        params: { period, year, employerCode },
        responseHandler: (response) => response.text(), // To handle non-json correctly if it's text, but original uses Accept: text/plain
        headers: { Accept: "text/plain" }
      }),
    }),
  }),
});

export const {
  useGetFileUploadsSummaryQuery,
  useGetFileUploadReferenceSummaryQuery,
  useGetPFAListQuery,
  useGetUploadPfaListQuery,
  useLazyGetUploadPfaListQuery,
  useGetUploadBeneficiariesQuery,
  useLazyGetUploadBeneficiariesQuery,
  useExportUploadBeneficiariesMutation,
  useGetUploadPfaItemsQuery,
  useValidateUploadMutation,
  useConfirmUploadMutation,
  useConfirmUploadOtpMutation,
  useDeleteUploadMutation,
  useGetPaymentHistoryQuery,
  useGetPFABeneficiariesQuery,
  useLazyGetPFABeneficiariesQuery,
  useGetPaymentsQuery,
  useCreateScheduleMutation,
  useUploadScheduleFileMutation,
  useCheckScheduleFileUploadStatusQuery,
  useRemoveItemFromScheduleMutation,
  useAddItemToScheduleMutation,
  usePfaListQuery,
  useGetEmployerInfoQuery,
  useGetPFAInfoQuery,
  useGetScheduleQuery,
  useLazyGetScheduleQuery,
  useConfirmScheduleQuery,
  useValidatePaymentOTPMutation,
  useValidatePaymentMutation,
  useReValidateCardPaymentMutation,
  useUpdateScheduleBeneficiaryMutation,
  useUploadEvidenceOfPaymentMutation,
  useDownloadEvidenceOfPaymentMutation,
  useDeleteRemitanceMutation,
  useGetPeymentScheduleBeneficiariesQuery,
  useGetPendingPayentsQuery,
  useGetPaymentApprovalOtpQuery,
  useLazyGetPaymentApprovalOtpQuery,
  useApprovePaymentOtpMutation,
  useGetPaymentSummaryQuery,
  useLazyGetPaymentSummaryQuery,
} = paymentsApi;
